import { useEffect, useRef, useState } from 'react';
import { GreyPanel, PreviewBanner } from 'modules/experiments/components/ui';
import { PANEL_HEIGHT, dataTestId } from 'modules/experiments/constants';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { useVariantConfigurationContext } from 'modules/experiments/contexts';
import { SelectVariantMode } from 'modules/experiments/types';
import { ExperimentTemplatePreview } from 'modules/templateEditor/integrations';

import { ContentActions } from './ContentActions';
import { PreviewSection } from './Styled';

type ContentProps = {
  loading: boolean;
};

export const Content = ({ loading }: ContentProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [useActionMenuForContentActions, setUseActionMenuForContentActions] = useState(false);
  const { t } = useExperimentsTranslations();
  const { selectedVariant, selectMode } = useVariantConfigurationContext();

  /**
   * When the content section is resized below 536px the content
   * action buttons will start overlapping with the title.
   * At this point we want to use an action menu instead of the
   * individual buttons.
   */
  useEffect(() => {
    const handleResize = () => {
      const container = panelRef.current;
      if (container) {
        const panelWidth = container?.getBoundingClientRect()?.width;
        setUseActionMenuForContentActions(panelWidth < 536);
      }
    };

    // Attach the resize event listener
    window.addEventListener('resize', handleResize);

    // Initial check
    handleResize();

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <GreyPanel
      testId={dataTestId.contentSection}
      ref={panelRef}
      title={t('panelHeading.content')}
      panelActions={<ContentActions useActionMenu={useActionMenuForContentActions} />}
      height={PANEL_HEIGHT.CONTENT}
      loading={loading}
      borderColor={selectMode === SelectVariantMode.EDIT ? selectedVariant?.textColor : undefined}
    >
      <PreviewBanner />
      <PreviewSection data-test={dataTestId.contentPreview}>
        <ExperimentTemplatePreview />
      </PreviewSection>
    </GreyPanel>
  );
};
